import classNames from 'classnames'
import { ComponentProps, useEffect, useState } from 'react'

import { randIntFromInterval } from '@ds'
import { getCDNUrl, useUserStar } from '@shared'

import styles from './Star.module.scss'
import { StarAnimation } from './components/StarAnimation'

const MAX_DELAY = 1000

export type StarSize = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'

interface StarProps extends ComponentProps<'div'> {
  size?: StarSize
  delay?: number
  onDisappear?: () => void
  disabled?: boolean
  onClick?: () => void
  src?: string
  shouldAnimate?: boolean
  isVisible?: boolean
  isActive?: boolean
}

const DEFAULT_STAR_LOTTIE_DATA = '/lottie/stars/star.lottie'

export function Star({
  size = 's',
  delay = randIntFromInterval(0, MAX_DELAY),
  onDisappear = () => {},
  onClick = () => {},
  className,
  disabled = false,
  src,
  shouldAnimate = true,
  isVisible = true,
  isActive = false,
  ...rest
}: StarProps) {
  const [starLottieData, setStarLottieData] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { userStar } = useUserStar()

  useEffect(() => {
    if (!src) {
      setIsLoading(true)
      const url = userStar?.appFiles
        ? getCDNUrl(userStar?.appFiles)
        : DEFAULT_STAR_LOTTIE_DATA

      fetch(url)
        .then((res) => res.arrayBuffer())
        .then((data) => {
          setStarLottieData(data)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('Failed to load star animation:', error)
          setIsLoading(false)
        })
      return
    }

    setIsLoading(true)

    const url = getCDNUrl(src)

    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((data) => {
        setStarLottieData(data)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Failed to load star animation:', error)
        setIsLoading(false)
      })
  }, [src, userStar])

  if (isLoading) {
    return null
  }

  if (!starLottieData) {
    return null
  }

  return (
    <StarAnimation
      starLottieData={starLottieData}
      delay={delay}
      onDisappear={onDisappear}
      onClick={onClick}
      isVisible={isVisible}
      disabled={disabled}
      shouldAnimate={shouldAnimate}
      isActive={isActive}
      className={classNames(styles.star, styles[`size-${size}`], className)}
      style={rest.style}
      id={rest.id}
    />
  )
}
